import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Center, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription, Box } from '@chakra-ui/react';
import VisitingCardComponent from './VisitingCardComponent';
import { useUserData } from '../hooks/useUserData';
import { generateQRCodeData, validateUserData } from '../utils/cardUtils';
import { generateCardMetaTags, generateStructuredData } from '../utils/metaUtils';

const VisitingCardPage = () => {
  const { email } = useParams();
  
  // Use useUserData for production, useMockUserData for development
  const { userData, loading, error } = useUserData(email, 'vc');
  // const { userData, loading, error } = useMockUserData(email);

  // Add cleanup on unmount to prevent stale function calls
  useEffect(() => {
    return () => {
      // Cleanup function to prevent errors during navigation
    };
  }, [email]);

  // Early return if no email parameter
  if (!email) {
    return (
      <Center minH="100vh" bg="gray.100">
        <Alert status="error" maxW="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Invalid URL!</AlertTitle>
            <AlertDescription>
              No email parameter provided in the URL.
            </AlertDescription>
          </Box>
        </Alert>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="100vh" bg="gray.100">
        <Alert status="error" maxW="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      </Center>
    );
  }

  // Validate user data
  const validation = validateUserData(userData, 'vc');
  if (!validation.isValid) {
    return (
      <Center minH="100vh" bg="gray.100">
        <Alert status="warning" maxW="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Invalid Data!</AlertTitle>
            <AlertDescription>
              {validation.errors.join(', ')}
            </AlertDescription>
          </Box>
        </Alert>
      </Center>
    );
  }

  const qrCodeData = generateQRCodeData(email, 'vc');

  // Generate meta data for social sharing using utility
  const metaTags = generateCardMetaTags(userData, 'vc', email);
  const structuredData = generateStructuredData(metaTags);

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={metaTags.type} />
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:image" content={metaTags.image} />
        <meta property="og:url" content={metaTags.url} />
        <meta property="og:site_name" content={metaTags.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.image} />
        
        {/* LinkedIn */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Additional meta tags for better sharing */}
        <meta name="author" content={metaTags.author} />
        <meta property="profile:first_name" content={metaTags.person.firstName} />
        <meta property="profile:last_name" content={metaTags.person.lastName} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <VisitingCardComponent 
        userData={userData}
        qrCodeData={qrCodeData}
        onDownload={() => {
          // Optional: Add analytics or logging here
          console.log('Visiting Card downloaded for:', userData.user_name);
        }}
      />
    </>
  );
};

export default VisitingCardPage;